import React, { useEffect, useState, useRef, MutableRefObject, useMemo } from 'react';
import NextImage from 'next/image';
import { useTheme } from 'styled-components';

import useBreakpoint from 'Common/hooks/useBreakpoint';

import { LinkProps } from 'Elements/Link';
import { H2 } from 'Elements/Typography';

import Grid, { Col } from 'Components/Grid';

import ListingCard, { AccommodationCardProps } from './subcomponents/ListingCard';

import mapImage from 'Public/images/accommodation-map/map.png';
import logo from 'Public/graphics/logo/logo.svg';

import {
    StyledSection,
    Description,
    StyledLink,
    WidgetWrapper,
    MapWrapper,
    StyledMap,
    LogoWrapper,
    MapButton,
    StyledIcon,
    FilterWrapper,
    Label,
    StyledSelect,
    ListingWrapper,
    Wave
} from './AccommodationMap.styled';

interface AccommodationListing extends AccommodationCardProps {
    mapPosition: {
        left: number;
        bottom: number;
    };
}

export interface Props {
    heading: string;
    description: string;
    viewAllLink: LinkProps;
    listings: AccommodationListing[];
}

// Helper function to keep consistent ids across the listings
export const convertNameToId = (name: string): string => {
    return name.toLowerCase().replace(' ', '-');
};

const AccommodationMap: React.FC<Props> = props => {
    const { heading, description, viewAllLink, listings } = props;

    const { breakpoints } = useTheme();
    const isLargeBreakpoint = useBreakpoint(breakpoints.large.value);
    const LISTING_HEIGHT_OFFSET = 52;

    const sortByTop = (a, b) => {
        return b.mapPosition.bottom - a.mapPosition.bottom;
    };

    // On first load sort the map buttons in tab order top to bottom
    useMemo(() => listings.sort(sortByTop), [listings]);

    const [listingHeight, setListingHeight] = useState<number>(0);
    const [listingOptions, setListingOptions] = useState<AccommodationListing[]>(listings);
    const [activeCard, setActiveCard] = useState<string>('');

    const mapRef = useRef() as MutableRefObject<HTMLDivElement>;
    const listingRef = useRef() as MutableRefObject<HTMLUListElement>;

    // Scroll to relevant card when map button is clicked
    useEffect(() => {
        const el = document.getElementById(activeCard);
        isLargeBreakpoint
            ? listingRef.current.scrollTo({
                  top: el?.offsetTop ? el.offsetTop - 16 : 0,
                  behavior: 'smooth'
              })
            : listingRef.current.scrollTo({
                  left: el?.offsetLeft ? el.offsetLeft - 32 : 0,
                  behavior: 'smooth'
              });
    }, [activeCard]);

    // Handle resizing the listing container when browser is resized
    useEffect(() => {
        function handleResize() {
            setListingHeight(mapRef.current.offsetHeight);
        }

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Sort listings by filter values
    const sortFunctions: {
        [key: string]: (a: AccommodationListing, b: AccommodationListing) => number;
    } = {
        'price-asc': (a, b) => (a.price < b.price ? -1 : 1),
        'price-desc': (a, b) => (b.price < a.price ? -1 : 1),
        'distance-asc': (a, b) => (a.distance < b.distance ? -1 : 1),
        'distance-desc': (a, b) => (b.distance < a.distance ? -1 : 1)
    };

    const sortListings = (value: string | null) => {
        value && setListingOptions(listingOptions.slice().sort(sortFunctions[value]));
    };

    return (
        <StyledSection id="accommodation-map" paddingTop={1} $listingHeight={listingHeight}>
            <Wave />
            <Grid>
                <Col l={8}>
                    <H2 size={{ base: '1.5rem', large: '2rem' }}>{heading}</H2>
                    <Description>{description}</Description>
                </Col>
                <Col l={4} justify="flex-end" align="flex-end">
                    <StyledLink {...viewAllLink} variant="text" bold />
                </Col>
            </Grid>
            <WidgetWrapper>
                <Grid style={{ columnGap: 0 }}>
                    <Col l={7}>
                        <MapWrapper ref={mapRef}>
                            <StyledMap
                                src={mapImage}
                                alt=""
                                width={1394}
                                height={937}
                                onLoad={() => setListingHeight(mapRef.current.offsetHeight)}
                            />
                            <LogoWrapper>
                                <NextImage
                                    src={logo}
                                    alt="Southampton Solent University logo"
                                    fill
                                />
                            </LogoWrapper>
                            {listingOptions.map(listing => {
                                const id = convertNameToId(listing.name);
                                return (
                                    <MapButton
                                        key={listing.name}
                                        data-name={id}
                                        onClick={() => setActiveCard(id)}
                                        $active={id === activeCard}
                                        aria-labelledby={id}
                                        $colour={listing.colour}
                                        tabIndex={id === activeCard ? -1 : 0}
                                        style={{
                                            left: `${listing.mapPosition.left}%`,
                                            bottom: `${listing.mapPosition.bottom}%`
                                        }}
                                    >
                                        <StyledIcon />
                                    </MapButton>
                                );
                            })}
                        </MapWrapper>
                    </Col>
                    <Col l={5}>
                        <FilterWrapper>
                            <Label htmlFor="filter-accommodation">Sort by:</Label>
                            <StyledSelect
                                id="filter-accommodation"
                                defaultValue="price"
                                name="filter-accomodation"
                                placeholder="Sort by..."
                                handleValueChange={value => sortListings(value)}
                                options={[
                                    { id: '1', value: 'price-asc', label: 'Price (ascending)' },
                                    { id: '2', value: 'price-desc', label: 'Price (descending)' },
                                    {
                                        id: '3',
                                        value: 'distance-asc',
                                        label: 'Distance (ascending)'
                                    },
                                    {
                                        id: '4',
                                        value: 'distance-desc',
                                        label: 'Distance (descending)'
                                    }
                                ]}
                            />
                        </FilterWrapper>
                        <ListingWrapper
                            id="listings"
                            ref={listingRef}
                            tabIndex={0}
                            style={{
                                height: isLargeBreakpoint
                                    ? `${listingHeight - LISTING_HEIGHT_OFFSET}px`
                                    : 'auto'
                            }}
                        >
                            {listingOptions.map(listing => (
                                <ListingCard
                                    key={listing.name}
                                    {...listing}
                                    activeCard={activeCard}
                                    setActiveCard={setActiveCard}
                                />
                            ))}
                        </ListingWrapper>
                    </Col>
                </Grid>
            </WidgetWrapper>
        </StyledSection>
    );
};

export default AccommodationMap;
